import { useCallback, useContext, useRef } from 'react';
import {
  getDockets,
  saveBumpedDocket,
  saveRecalledDocket,
  saveDocketsOverwrite,
  saveDockets,
  swapUpdatedDockets,
  getAllDockets,
  repositionDocket,
} from '../storage/docket';
import config from '../config';
import {
  Docket,
  DocketItemStatus,
  DocketStatus,
  OrderType,
  NotifierDocketItems,
  KitchenDisplayMode,
} from '@oolio-group/domain';
import { DocketSource } from '../common/types';
import { useOrgTimings } from './useOrgTimings';
import { getStartTimeandEndTime } from '../utils/timeHelper';
import { DocketBumper } from '../screens/Main/Dockets/DocketBumper';
import { makeRequest } from '../utils/fetchHelper';
import {
  openDocketslastUpdatedAt,
  selectedDocketVar,
  docketsLastMovedAt,
  docketMoving,
  autoBumpFeatureVar,
} from '../state/apolloVars';
import {
  useOrderNotification,
  retainDocketGuards,
  releaseDocketGuards,
} from './useOrderNotification';
import { useSession } from './useSession';
import { usePrintingWithTemplate } from './printing/usePrintingWithTemplate';
import { sendNotification } from './useNotification';
import { generatePrinterOptions } from './printing/generatePrinterOption';
import { useSalesChannels } from './useSalesChannels';
import { useOrderTypes } from './useOrderTypes';
import { usePrinterProfiles } from './usePrinterProfiles';
import { useDocketFilter } from './useDocketFilter';
import { useFilterKey } from './useFilterKey';
import { DocketPlacement, SortingPreference } from '../common/constants';
import { debounce } from 'lodash';
import {
  shouldAutoBumpDocket,
  getAutoBumpStatus,
  autoBumpAttemptGuard,
  submitAutoBumpBatch,
} from '../utils/docketHelper';

export interface UseDockets {
  getActiveDockets: (
    printerProfileIds: string[],
    storeId: string,
    orgId?: string,
  ) => Promise<Docket[]>;
  getCompletedDockets: (
    printerProfileIds: string[],
    storeId: string,
    orgId?: string,
  ) => Promise<Docket[]>;
  getDockets: (type: DocketStatus) => Promise<Docket[]>;
  bumpDocketWithStatus: (
    id: string,
    status: DocketItemStatus,
    notifiers?: NotifierDocketItems[],
  ) => Promise<Docket>;
  recallDocket: (id: string) => Promise<Docket>;
  bumpDocketItem: (id: string) => Promise<Docket>;
  bumpDocketItems: (ids: string[]) => Promise<void>;
  serveDocketItem: (id: string) => Promise<Docket>;
  reBumpDocketItem: (id: string) => Promise<Docket>;
  recallDocketItem: (id: string) => Promise<Docket>;
  printKitchenDocket: (
    docket: string,
    status: DocketStatus,
    notifiers?: NotifierDocketItems[],
  ) => Promise<void>;
  ackDocketsReceived: (dockets?: Docket[]) => Promise<void>;
  tagDockets: (
    status: DocketStatus,
    ids: string[],
    tags: string[],
  ) => Promise<{ error: string }>;
  untagDockets: (
    status: DocketStatus,
    ids: string[],
    tags: string[],
  ) => Promise<{ error: string }>;
  rushDockets: (ids: string[]) => Promise<{ error: string }>;
  unrushDockets: (ids: string[]) => Promise<{ error: string }>;
  moveDocket: (id: string, placement: DocketPlacement) => Promise<void>;
}

export function useDockets(): UseDockets {
  const { dateTime } = useOrgTimings();
  const context = useContext(DocketBumper);
  const { sendBumpSmsNotification, sendReceiveSmsNotification } =
    useOrderNotification();
  const { session } = useSession();
  const { notifyOnBump, notifyOnReceive } =
    session.kitchenDisplay?.notificationConfig || {};

  const { printKitchenDocket } = usePrintingWithTemplate();

  const { salesChannels } = useSalesChannels();
  const { orderTypes } = useOrderTypes();
  const { printerProfiles } = usePrinterProfiles();

  const { selectedDeviceIds, selectedSectionIds } = useDocketFilter();

  const ackDocketsReceived = useCallback(
    async (dockets: Docket[] = []) => {
      const receivedBy = session.kitchenDisplay?.id || '';

      const docketsToAck = dockets.filter(
        docket =>
          !docket?.receivedAcks?.find(ack => ack.receivedBy === receivedBy),
      );

      if (
        docketsToAck.length &&
        session.kitchenDisplay?.id &&
        session.kitchenDisplay?.name
      ) {
        const input = {
          ids: docketsToAck.map(docket => docket.id),
          receivedBy: session.kitchenDisplay.id || '',
          receivedByName: session.kitchenDisplay?.name || '',
        };

        return await makeRequest(
          config.KDS_SERVICE_URI + '/dockets/received',
          input,
        );
      }
    },
    [session.kitchenDisplay?.name, session.kitchenDisplay?.id],
  );

  const { filterKey, isStale } = useFilterKey(
    selectedDeviceIds,
    selectedSectionIds,
  );

  const getDocketsByPrinterProfileAndStatus = useCallback(
    //TODO add store id in route or in headers
    async (
      ids: string[],
      status: DocketStatus,
      storeId: string,
      orgId?: string,
    ) => {
      const timings = getStartTimeandEndTime(dateTime?.startTime || '');
      const input = {
        ids,
        status: status,
        ...(status == DocketStatus.COMPLETED && {
          startTime: timings.startTime,
          endTime: timings.endTime,
        }),
        ...(storeId && { storeId }),
        ...(orgId && { orgId }),
        ...(selectedDeviceIds?.length ? { deviceIds: selectedDeviceIds } : {}),
        ...(selectedSectionIds?.length
          ? { sectionIds: selectedSectionIds }
          : {}),
      };
      const data = await makeRequest(
        config.KDS_SERVICE_URI + '/dockets',
        input,
      );
      if (isStale(filterKey)) {
        return undefined;
      }
      // A failed ({}) response returns undefined so callers don't clear guards as if nothing is active.
      const hasDocketList = Array.isArray(data?.dockets);
      if (!hasDocketList) return undefined;
      const dockets = data.dockets as Docket[];
      // [] under an active filter means "all hidden", so still overwrite storage.
      const isFiltered =
        !!selectedDeviceIds?.length || !!selectedSectionIds?.length;
      if (dockets.length || isFiltered) {
        await saveDocketsOverwrite(dockets, status, DocketSource.API);
      }
      return dockets;
    },
    [
      dateTime?.startTime,
      selectedDeviceIds,
      selectedSectionIds,
      filterKey,
      isStale,
    ],
  );

  const getCompletedDockets = useCallback(
    async (printerProfileIds: string[], storeId: string, orgId?: string) => {
      return (
        (await getDocketsByPrinterProfileAndStatus(
          printerProfileIds,
          DocketStatus.COMPLETED,
          storeId,
          orgId,
        )) || []
      );
    },
    [getDocketsByPrinterProfileAndStatus],
  );

  const getSalesChannelName = useCallback(
    (docket: Docket | undefined): string => {
      const salesChannelId = docket?.salesChannel?.id;
      const salesChannel =
        salesChannels.find(s => s.id === salesChannelId)?.name || '';
      return salesChannel;
    },
    [salesChannels],
  );

  const getOrderTypeFromId = useCallback(
    (id: string | undefined): OrderType | undefined => {
      if (!id) {
        return undefined;
      }
      return orderTypes.find(o => o.id === id) as OrderType;
    },
    [orderTypes],
  );

  const printDocket = useCallback(
    async (docket?: Docket) => {
      const newDocket = {
        ...docket,
        ...(docket?.salesChannel && {
          salesChannel: {
            ...docket.salesChannel,
            name: getSalesChannelName(docket),
          },
        }),
        ...(docket?.orderType?.id && {
          orderType: {
            id: docket.orderType.id,
            ...getOrderTypeFromId(docket.orderType.id),
          },
        }),
      } as Docket;
      const printer = session?.kitchenDisplay?.printer;
      if (newDocket && printer) {
        const printerProfile = printerProfiles.find(
          e => e.id === newDocket.printerProfileId,
        );
        const result = await printKitchenDocket(
          newDocket,
          generatePrinterOptions(
            printer,
            session?.kitchenDisplay?.singleItemPrinting || false,
            printerProfile,
          ),
        );
        if (result) {
          sendNotification(result);
        }
      }
    },
    [
      getOrderTypeFromId,
      getSalesChannelName,
      printKitchenDocket,
      printerProfiles,
      session?.kitchenDisplay?.printer,
      session?.kitchenDisplay?.singleItemPrinting,
    ],
  );

  const printDocketById = useCallback(
    async (
      docketId: string,
      status: DocketStatus,
      notifiers?: NotifierDocketItems[],
    ) => {
      const dockets = await getDockets(status);
      const docket = dockets.find(d => d.id === docketId);
      if (docket) {
        await printDocket({
          ...docket,
          notifierItems: notifiers,
        });
      } else {
        //if docket is not found by status, try to get docket by id
        const allDockets = await getAllDockets();
        const docket = allDockets.find(d => d?.id === docketId);
        if (docket) {
          await printDocket({
            ...docket,
            notifierItems: notifiers,
          });
        }
      }
    },
    [printDocket],
  );

  const updateTagsLocally = async (
    tagUpdates: { id: string; tags: string[] }[],
    status: DocketStatus,
  ) => {
    const dockets = await getDockets(status); // read from local storage
    const updatedDockets = dockets.map(doc => {
      const updated = tagUpdates.find(item => item.id === doc.id);
      return updated ? { ...doc, tags: updated.tags } : doc;
    });
    saveDocketsOverwrite(updatedDockets, status, DocketSource.API); // set to local storage
    openDocketslastUpdatedAt(performance.now()); // update the dockets dashboard

    const selectedDocketMap = selectedDocketVar();
    const updatedDocketMap = tagUpdates.reduce(
      (acc, { id, tags }) => {
        if (acc[id]) {
          acc[id] = { ...acc[id], tags };
        }
        return acc;
      },
      { ...selectedDocketMap },
    );

    selectedDocketVar(updatedDocketMap);
  };

  const tagDockets = useCallback(
    async (
      status: DocketStatus,
      docketIds: string[],
      tags: string[],
      clearExistingTags = false,
    ): Promise<{ error: string }> => {
      const input = { ids: docketIds, tags, clearExistingTags };
      const resp = await makeRequest(
        config.KDS_SERVICE_URI + '/dockets/tag',
        input,
      );
      if (resp?.error) return { error: resp.error };
      resp?.data?.length && (await updateTagsLocally(resp.data, status));
      return { error: '' };
    },
    [],
  );

  const untagDockets = useCallback(
    async (
      status: DocketStatus,
      docketIds: string[],
      tags: string[],
    ): Promise<{ error: string }> => {
      const input = { ids: docketIds, tags };
      const resp = await makeRequest(
        config.KDS_SERVICE_URI + '/dockets/untag',
        input,
      );
      if (resp?.error) return { error: resp.error };
      resp?.data?.length && (await updateTagsLocally(resp.data, status));
      return { error: '' };
    },
    [],
  );

  const rushDockets = useCallback(async (docketIds: string[]) => {
    const input = { ids: docketIds };
    const resp: {
      error: string;
      success: boolean;
      data: { id: string; rushedAt: number }[];
    } = await makeRequest(config.KDS_SERVICE_URI + '/dockets/rush', input);
    if (resp?.error) return { error: resp.error };

    if (resp?.data?.length) {
      const dockets = await getDockets(DocketStatus.CREATED);
      const updatedDockets = dockets.map(doc => {
        const updated = resp.data.find(item => item.id === doc.id);
        return updated ? { ...doc, rushedAt: updated.rushedAt } : doc;
      });
      saveDocketsOverwrite(
        updatedDockets,
        DocketStatus.CREATED,
        DocketSource.API,
      );
      openDocketslastUpdatedAt(performance.now());
    }

    return { error: '' };
  }, []);

  const unrushDockets = useCallback(async (docketIds: string[]) => {
    const input = { ids: docketIds };
    const resp = await makeRequest(
      config.KDS_SERVICE_URI + '/dockets/unrush',
      input,
    );
    if (resp?.error) return { error: resp.error };

    const dockets = await getDockets(DocketStatus.CREATED);
    const updatedDockets = dockets.map(doc => {
      return docketIds.includes(doc.id) ? { ...doc, rushedAt: 0 } : doc;
    });
    saveDocketsOverwrite(
      updatedDockets,
      DocketStatus.CREATED,
      DocketSource.API,
    );
    openDocketslastUpdatedAt(performance.now());

    return { error: '' };
  }, []);

  const bumpDocketWithStatus = useCallback(
    async (
      docketId: string,
      status: DocketItemStatus,
      notifiers?: NotifierDocketItems[],
    ) => {
      const input = {
        ids: [docketId],
        status: status,
      } as { ids: string[] };
      const dockets = await makeRequest(
        config.KDS_SERVICE_URI + '/docket/bump',
        input,
      );
      if (session?.kitchenDisplay?.autoPrintOnCompletion === true) {
        if (dockets?.[0]) {
          await printDocket({
            ...dockets[0],
            notifierItems: notifiers,
          });
        }
      }
      if (
        notifyOnBump &&
        dockets?.length &&
        !dockets?.[0].notificationStatus?.smsOnBump
      ) {
        sendBumpSmsNotification([docketId]);
      }
      if (dockets?.length) {
        saveBumpedDocket(dockets[0]);
        if (context.current)
          context.current = context.current.filter(c => c != dockets[0].id);
        openDocketslastUpdatedAt(performance.now());
        return dockets[0] as Docket;
      }
      return {} as Docket;
    },
    [
      context,
      notifyOnBump,
      printDocket,
      sendBumpSmsNotification,
      session?.kitchenDisplay?.autoPrintOnCompletion,
    ],
  );

  const getActiveDockets = useCallback(
    async (printerProfileIds: string[], storeId: string, orgId?: string) => {
      const dockets = await getDocketsByPrinterProfileAndStatus(
        printerProfileIds,
        DocketStatus.CREATED,
        storeId,
        orgId,
      );
      if (!dockets) return [];

      retainDocketGuards(dockets.map(docket => docket.id));

      // Check dockets and auto bump if needed
      if (notifyOnReceive) {
        const unNotifiedDockets = dockets.filter(
          d => !d?.notificationStatus?.smsOnReceive,
        );
        if (unNotifiedDockets?.length) {
          sendReceiveSmsNotification(unNotifiedDockets.map(item => item.id));
        }
      }

      // Auto-bump check based on device type
      const mode = session?.kitchenDisplay?.mode || KitchenDisplayMode.PREP;
      const rebumpToggle = session?.rebumpToggle || false;

      const docketIdsToAutoBump = dockets
        .filter(docket =>
          shouldAutoBumpDocket(
            docket,
            mode,
            rebumpToggle,
            autoBumpFeatureVar(),
          ),
        )
        .map(docket => docket.id);

      await submitAutoBumpBatch(
        docketIdsToAutoBump,
        getAutoBumpStatus(rebumpToggle),
      );

      return dockets;
    },
    [
      getDocketsByPrinterProfileAndStatus,
      notifyOnReceive,
      sendReceiveSmsNotification,
      session,
    ],
  );

  /**
   * Check if docket should be auto-bumped after item status change
   * and auto-bump if all items are complete
   */
  const checkAndAutoBumpDocket = useCallback(
    async (docket: Docket) => {
      const mode = session?.kitchenDisplay?.mode || KitchenDisplayMode.PREP;
      const rebumpToggle = session?.rebumpToggle || false;

      if (
        shouldAutoBumpDocket(
          docket,
          mode,
          rebumpToggle,
          autoBumpFeatureVar(),
        ) &&
        autoBumpAttemptGuard.filterUnattempted([docket.id]).length
      ) {
        autoBumpAttemptGuard.markAttempted([docket.id]);
        const result = await bumpDocketWithStatus(
          docket.id,
          getAutoBumpStatus(rebumpToggle),
        );
        if (!result?.id) {
          autoBumpAttemptGuard.release([docket.id]);
        }
      }
    },
    [session, bumpDocketWithStatus],
  );

  const bumpDocketItem = useCallback(
    async (docketItemId: string) => {
      const input = {
        id: docketItemId,
      } as { id: string };
      const dockets = await makeRequest(
        config.KDS_SERVICE_URI + '/docket-item/bump',
        input,
      );
      if (dockets?.length) {
        saveDockets(docketItemId, dockets, DocketSource.API);
        openDocketslastUpdatedAt(performance.now());

        // Check if docket should be auto-bumped
        await checkAndAutoBumpDocket(dockets[0]);

        return dockets[0] as Docket;
      }
      return {} as Docket;
    },
    [checkAndAutoBumpDocket],
  );

  const bumpDocketItems = useCallback(
    async (docketItemIds: string[]) => {
      const input = {
        ids: docketItemIds,
      } as { ids: string[] };
      const dockets = await makeRequest(
        config.KDS_SERVICE_URI + '/docket-items/bump',
        input,
      );
      if (dockets?.length) {
        await swapUpdatedDockets(dockets, DocketSource.API);
        openDocketslastUpdatedAt(performance.now());

        // Collect all docket IDs that need auto-bumping
        const mode = session?.kitchenDisplay?.mode || KitchenDisplayMode.PREP;
        const rebumpToggle = session?.rebumpToggle || false;

        const docketIdsToAutoBump = dockets
          .filter((docket: Docket) =>
            shouldAutoBumpDocket(
              docket,
              mode,
              rebumpToggle,
              autoBumpFeatureVar(),
            ),
          )
          .map((docket: Docket) => docket.id);

        await submitAutoBumpBatch(
          docketIdsToAutoBump,
          getAutoBumpStatus(rebumpToggle),
        );
      }
    },
    [session],
  );

  const serveDocketItem = useCallback(
    async (docketItemId: string) => {
      const input = {
        id: docketItemId,
      } as { id: string };
      const dockets = await makeRequest(
        config.KDS_SERVICE_URI + '/docket-item/serve',
        input,
      );
      if (dockets?.length) {
        saveDockets(docketItemId, dockets, DocketSource.API);
        openDocketslastUpdatedAt(performance.now());

        // Check if docket should be auto-bumped
        await checkAndAutoBumpDocket(dockets[0]);

        return dockets[0] as Docket;
      }
      return {} as Docket;
    },
    [checkAndAutoBumpDocket],
  );

  const reBumpDocketItem = useCallback(
    async (docketItemId: string) => {
      const input = {
        id: docketItemId,
      } as { id: string };
      const dockets = await makeRequest(
        config.KDS_SERVICE_URI + '/docket-item/rebump',
        input,
      );
      if (dockets?.length) {
        saveDockets(docketItemId, dockets, DocketSource.API);
        openDocketslastUpdatedAt(performance.now());

        // Check if docket should be auto-bumped
        await checkAndAutoBumpDocket(dockets[0]);

        return dockets[0] as Docket;
      }
      return {} as Docket;
    },
    [checkAndAutoBumpDocket],
  );

  const recallDocketItem = useCallback(async (docketItemId: string) => {
    const input = {
      id: docketItemId,
    } as { id: string };
    const dockets = await makeRequest(
      config.KDS_SERVICE_URI + '/docket-item/recall',
      input,
    );
    if (dockets?.length) {
      releaseDocketGuards([dockets[0].id]);
      saveRecalledDocket(dockets[0]);
      openDocketslastUpdatedAt(performance.now());
      return dockets[0] as Docket;
    }
    return {} as Docket;
  }, []);

  const recallDocket = useCallback(async (docketId: string) => {
    const input = {
      id: docketId,
    } as { id: string };
    const docket = await makeRequest(
      config.KDS_SERVICE_URI + '/docket/recall',
      input,
    );
    if (docket?.id) {
      releaseDocketGuards([docket.id]);
      saveRecalledDocket(docket);
      openDocketslastUpdatedAt(performance.now());

      return docket as Docket;
    }
    return {} as Docket;
  }, []);

  const debouncedMoveRequest = useRef(
    debounce(async (input: { id: string; offset: number }) => {
      await makeRequest(`${config.KDS_SERVICE_URI}/dockets/move`, input);
      docketMoving(false);
    }, 1000),
  ).current;

  const moveDocket = useCallback(
    async (docketId: string, placement: DocketPlacement) => {
      docketMoving(true);
      const dockets = await getDockets(DocketStatus.CREATED);
      const offset = await repositionDocket(
        dockets,
        docketId,
        placement,
        session?.sortingPref?.value as SortingPreference,
      );
      openDocketslastUpdatedAt(performance.now());
      docketsLastMovedAt(performance.now());
      const input = { id: docketId, offset };

      debouncedMoveRequest(input);
    },
    [debouncedMoveRequest, session?.sortingPref?.value],
  );

  return {
    getActiveDockets,
    getCompletedDockets,
    getDockets,
    bumpDocketWithStatus,
    recallDocket,
    bumpDocketItem,
    bumpDocketItems,
    recallDocketItem,
    serveDocketItem,
    reBumpDocketItem,
    printKitchenDocket: printDocketById,
    ackDocketsReceived,
    tagDockets,
    untagDockets,
    rushDockets,
    unrushDockets,
    moveDocket,
  };
}
